import ApplicationController from './application_controller'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import Compressor from '@uppy/compressor'
import ThumbnailGenerator from '@uppy/thumbnail-generator'

/**
 * File Upload Controller
 *
 * This controller handles file uploads with direct-to-S3 functionality using Uppy.
 * It's used for various file upload scenarios including account brand images
 * and publication import files.
 *
 * Required Targets:
 * - fileInput: The file input element that holds the selected file
 * - hiddenInput: Hidden input that stores the JSON metadata of the uploaded file
 *
 * Optional Targets:
 * - trigger: Button that opens the file picker (clicks the fileInput when triggered).
 *   Only present in the button-triggered layout (UI::Form::ImageUploadComponent),
 *   where fileInput is hidden. Forms with a visible fileInput omit it.
 * - filename: Element that displays the selected filename
 * - previewImage: Image element that displays the thumbnail preview
 * - errorMessage: Element that displays upload error messages
 *
 * Values:
 * - maxFileSize: Maximum allowed file size in bytes
 * - presignUrl: URL to get presigned S3 upload parameters
 * - allowedFileTypes: Array of allowed file extensions and MIME types
 * - resizeMaxDimension: Optional longest edge (px) to shrink images to before upload;
 *   HEIC becomes JPEG where the browser can decode it, otherwise uploads as picked
 *
 * @example
 * <div data-controller="file-upload"
 *      data-file-upload-max-file-size-value="10485760"
 *      data-file-upload-presign-url-value="/presigns/path"
 *      data-file-upload-allowed-file-types-value='[".pdf", "application/pdf"]'>
 *   <input type="file" name="asset[file]" data-file-upload-target="fileInput" data-action="change->file-upload#uploadFile" class="hidden">
 *   <input type="hidden" name="asset[file]" data-file-upload-target="hiddenInput">
 *   <button type="button" data-file-upload-target="trigger">Choose File</button>
 *   <span data-file-upload-target="filename"></span>
 *   <img data-file-upload-target="previewImage" class="hidden">
 *   <p data-file-upload-target="errorMessage" class="hidden mt-0.5 prose prose-sm max-w-none prose-a:text-blue-600 text-red-600"></p>
 * </div>
 */
export default class extends ApplicationController {
  static targets = [
    'fileInput',
    'hiddenInput',
    'filename',
    'previewImage',
    'errorMessage',
    'trigger',
  ]

  static values = {
    maxFileSize: Number,
    presignUrl: String,
    allowedFileTypes: Array,
    resizeMaxDimension: Number,
  }

  /**
   * Initialize the controller and Uppy instance
   * Verifies required targets and exits early if they're missing
   */
  connect() {
    // Check for required targets early
    if (!this.hasFileInputTarget) {
      this.reportError(
        new Error('Missing required target: fileInput'),
        'Controller initialization failed: fileInput target is required',
      )
      return
    }

    if (!this.hasHiddenInputTarget) {
      this.reportError(
        new Error('Missing required target: hiddenInput'),
        'Controller initialization failed: hiddenInput target is required',
      )
      return
    }

    this.updateFilenameFromCachedData()

    this.configs = {
      maxFileSize: this.maxFileSizeValue,
      allowedFileTypes: this.hasAllowedFileTypesValue ? this.allowedFileTypesValue : null,
    }

    this.uppy = this.initUppy()
    this.initUppyAws()
    this.setupUppyEvents()
  }

  /**
   * Set up trigger button click handler if trigger target exists
   */
  triggerTargetConnected() {
    this.triggerTarget.addEventListener('click', (e) => {
      e.preventDefault()
      this.fileInputTarget.click()
    })
  }

  /**
   * Clean up Uppy instance when controller is disconnected
   */
  disconnect() {
    if (!this.uppy) return
    this.uppy.destroy()
  }

  /**
   * Handles file selection from the input element
   * Adds selected files to Uppy for processing
   */
  uploadFile(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    this.hideErrorMessage()

    files.forEach((file) => {
      try {
        this.uppy.addFile({
          source: 'file input',
          name: file.name,
          type: file.type,
          data: file,
        })

        this.updateFilenameDisplay(file.name)
      } catch (err) {
        // addFile emits restriction-failed AND throws — the listener already
        // handled a restriction error, so only act on genuine failures here.
        if (!err.isRestriction) this.handleUploadError(err)
      }
    })
  }

  /**
   * Creates and configures the Uppy instance with file restrictions
   * @returns {Uppy} Configured Uppy instance
   */
  initUppy() {
    let uppy = new Uppy({
      id: this.fileInputTarget.id,
      autoProceed: true,
      restrictions: {
        maxFileSize: this.configs.maxFileSize,
        minFileSize: 1,
        allowedFileTypes: this.configs.allowedFileTypes,
      },
    })

    // Add ThumbnailGenerator plugin
    uppy.use(ThumbnailGenerator, {
      thumbnailWidth: 300,
    })

    if (this.hasResizeMaxDimensionValue) {
      uppy.use(Compressor, {
        maxWidth: this.resizeMaxDimensionValue,
        maxHeight: this.resizeMaxDimensionValue,
        quality: 0.9,
        convertTypes: ['image/heic'],
        convertSize: 0,
      })
    }

    return uppy
  }

  /**
   * Adds AWS S3 plugin to Uppy for direct-to-S3 uploads
   * Makes a request to our backend to get presigned URL parameters
   */
  initUppyAws() {
    this.uppy.use(AwsS3, {
      shouldUseMultipart: () => false,
      getUploadParameters: (file) => {
        const filename = encodeURIComponent(file.meta.name)
        const type = encodeURIComponent(file.meta.type)
        return fetch(`${this.presignUrlValue}?filename=${filename}&type=${type}`).then(
          (response) => {
            if (!response.ok) {
              throw new Error(`Server responded with ${response.status}`)
            }
            return response.json()
          },
        )
      },
    })
  }

  /**
   * Sets up event handlers for Uppy upload events
   */
  setupUppyEvents() {
    // The 'upload-success' event provides two parameters: file (UppyFile) and response (object)
    this.uppy.on('upload-success', this.handleUploadSuccess.bind(this))
    // The 'upload-error' event provides three parameters: file (UppyFile), error (Error) and response (object)
    this.uppy.on('upload-error', this.handleUppyUploadError.bind(this))
    // The 'complete' event provides one parameter: result (object) with successful and failed arrays
    this.uppy.on('complete', this.handleUploadComplete.bind(this))
    // The 'restriction-failed' event provides two parameters: file (UppyFile) and error (Error)
    this.uppy.on('restriction-failed', this.handleRestrictionFailed.bind(this))
    // The 'error' event provides one parameter: error (Error)
    // Uppy errors must be manually reported because they don't bubble up
    // to the global error handler or Stimulus's installErrorHandler
    this.uppy.on('error', (error) => {
      this.reportError(error, { source: 'uppy' })
    })

    this.initUppyThumbnail()
  }

  /**
   * Handles successful file uploads
   * Updates the hidden input with file metadata and clears the file input
   */
  handleUploadSuccess(file, response) {
    // Add breadcrumb for successful upload
    this.addBreadcrumb('upload', 'upload-success', {
      fileId: file.id,
      fileName: file.name,
    })

    if (response && response.body && response.body.key) {
      file.meta['key'] = response.body.key
    }
    this.setHiddenInput(file)
    this.clearFileInput()
  }

  /**
   * Handles upload errors from Uppy
   * Reports the error and displays an error message
   */
  handleUppyUploadError(file, error, response) {
    // Add breadcrumb for upload error context
    this.addBreadcrumb('upload', 'upload-error', {
      fileId: file.id,
      fileName: file.name,
      fileType: file.type,
      isNetworkError: error.isNetworkError || false,
      responseStatus: response?.status,
      errorMessage: error.message,
    })

    const errorMessage = 'Upload failed. Please try again.'
    this.reportError(error, errorMessage, { file })
    this.showErrorMessage(errorMessage)
    this.resetFilenameDisplay()
  }

  /**
   * Handles upload completion
   * Clears the file input to allow re-selection of the same file
   */
  handleUploadComplete(result) {
    // Add breadcrumb for complete upload
    this.addBreadcrumb('upload', 'complete', {
      successfulCount: result && result.successful ? result.successful.length : 0,
      failedCount: result && result.failed ? result.failed.length : 0,
    })

    this.clearFileInput()
  }

  /**
   * Handles file restriction failures (size, type) from the restriction-failed
   * event — expected user-facing validation: surface the message, no report.
   */
  handleRestrictionFailed(file, error) {
    this.addBreadcrumb('upload', 'restriction-failed', {
      fileName: file.name,
      error: error.message,
    })

    this.showErrorMessage(`File error: ${error.message}`)
    this.clearFileInput()
  }

  /**
   * Sets up thumbnail generation for image uploads
   * Updates the previewImage target with the generated thumbnail
   */
  initUppyThumbnail() {
    // Track file added events with breadcrumbs
    // The 'file-added' event provides one parameter: file (UppyFile)
    this.uppy.on('file-added', (file) => {
      this.addBreadcrumb('upload', 'file-added', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      })
    })

    // Track upload start events with breadcrumbs
    // The 'upload' event provides two parameters: uploadID (string) and files (UppyFile[])
    this.uppy.on('upload', (uploadID, files) => {
      this.addBreadcrumb('upload', 'upload-started', {
        uploadId: uploadID,
        fileCount: files.length,
      })
    })

    // Track upload retry events
    // The 'upload-retry' event provides one parameter: file (UppyFile)
    this.uppy.on('upload-retry', (file) => {
      this.addBreadcrumb('upload', 'upload-retry', {
        fileId: file.id,
      })
    })
    // The 'thumbnail:generated' event provides two parameters: file (UppyFile) and preview (string)
    this.uppy.on('thumbnail:generated', (file, preview) => {
      if (this.hasPreviewImageTarget) {
        this.previewImageTarget.src = preview
        // Make sure the image is visible
        this.previewImageTarget.classList.remove('hidden')
      }
    })
  }

  /**
   * Updates the hidden input with the JSON metadata of the uploaded file
   */
  setHiddenInput(file) {
    this.hiddenInputTarget.value = this.uploadedFileData(file)
  }

  /**
   * Formats the file data in the format Shrine expects
   * @returns {string} JSON string with file metadata
   */
  uploadedFileData(file) {
    // Check if key exists
    if (!file.meta || !file.meta['key']) {
      throw new Error('Could not find key in file metadata')
    }

    // Extract the ID from the key
    const match = file.meta['key'].match(/^cache\/(.+)/)
    if (!match) {
      throw new Error(`Invalid key format: ${file.meta['key']}`)
    }

    return JSON.stringify({
      id: match[1], // object key without prefix
      storage: 'cache',
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type,
      },
    })
  }

  /**
   * Displays the filename from cached data when the controller initializes
   * Used to show previously uploaded filenames
   */
  updateFilenameFromCachedData() {
    if (!this.hiddenInputTarget.value || !this.hasFilenameTarget) return

    try {
      const fileData = JSON.parse(this.hiddenInputTarget.value)
      if (fileData?.metadata?.filename) {
        this.updateFilenameDisplay(fileData.metadata.filename)
      }
    } catch (e) {
      this.reportError(e, 'Error parsing file data')
    }
  }

  /**
   * Updates the filename display element with the selected filename
   */
  updateFilenameDisplay(filename) {
    if (!this.hasFilenameTarget) return
    this.filenameTarget.textContent = filename
  }

  /**
   * Clears the filename display element
   */
  resetFilenameDisplay() {
    if (!this.hasFilenameTarget) return
    this.filenameTarget.textContent = ''
  }

  /**
   * Handles a genuine upload error — surfaces a user-friendly message and
   * reports it. Restriction failures are handled by handleRestrictionFailed.
   */
  handleUploadError(error) {
    const errorMessage = error.message || 'Upload failed. Please try again.'

    this.reportError(error, errorMessage)
    this.showErrorMessage(errorMessage)
    this.clearFileInput()
  }

  /**
   * Clears the file input value
   * This is necessary to allow re-selection of the same file
   */
  clearFileInput() {
    this.fileInputTarget.value = ''
  }

  /**
   * Displays an error message in the errorMessage target
   */
  showErrorMessage(message) {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove('hidden')
  }

  /**
   * Hides the error message element
   */
  hideErrorMessage() {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = ''
    this.errorMessageTarget.classList.add('hidden')
  }
}
